import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useMessage } from '@pepper/ui'
import axios from '@pepper/utils/src/function/axiosInstance'
import extract from '@pepper/utils/src/function/extract'

import type { GapReport } from '@pepper/utils/src/types/seo'
import { SEO_SERVICE_BASE_URL } from '@pepper/utils/src/api/config'

type ReturnType = {
  data: GapReport | undefined
  isLoading: boolean
  isFetching: boolean
  refetch: () => void
  error: unknown
}

const STALE_TIME_MS = 300000 // 5mins

async function handleApiRequest({
  reportId,
  companyId
}: {
  reportId: string
  companyId: string
}) {
  const url = `${SEO_SERVICE_BASE_URL}/companies/${companyId}/content-gap/${reportId}`

  try {
    const response = await axios.get(url)
    return extract.data<GapReport>(response).data
  } catch (error) {
    throw new Error(extract.error(error).data)
  }
}

export const useGetGapReport = ({
  companyId,
  reportId
}: {
  companyId: string
  reportId: string
}): ReturnType => {
  const { showError } = useMessage()

  const { isLoading, isFetching, isError, error, refetch, data } = useQuery<
    GapReport,
    Error
  >({
    queryKey: ['content-report', companyId, reportId],
    queryFn: () => handleApiRequest({ companyId, reportId }),
    enabled: !!companyId && !!reportId,
    retry: 3
  })

  useEffect(() => {
    if (isError) {
      showError(error?.message)
    }
  }, [isError, error, showError])

  return {
    data,
    isLoading,
    isFetching,
    refetch,
    error
  }
}
